import React, { useMemo, useState } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import { Autocomplete, Box } from '@mui/material';
import toast from 'react-hot-toast';
import { useGetSensorsValuesTypesQuery } from '../../../../../../entities/equipments/sensorsValuesTypesResource/redux/sensorsValuesTypesResource.api';
import defaultAutocompleteProps from './config';
import hasDuplicateById from '../../../../../../shared/utils/hasDuplicateById';
import SensorGroupSelect from './SensorGroupSelect';

// Поле выбора сенсоров с группировкой и проверкой дубликатов
function SensorsField({ data = [], setData, vehicleId }) {
  const params = { 
      "isRaw.equals":"false", 
  }
  if (vehicleId) {
      params["vehicleId.equals"] = vehicleId
  }
  const { data: { data: options = [] } = {}, isLoading } = useGetSensorsValuesTypesQuery(params);

  const [selectedGroup, setSelectedGroup] = useState('all');

  // Обрабатывает изменение выбранных сенсоров с проверкой лимита и дубликатов
  const handleChange = (_event, newValue, _target, arg) => {
    const isDuplicate = hasDuplicateById(newValue, arg?.option?.sensorTypeId, 'sensorTypeId');
    if (isDuplicate) return;

    const { limitTags } = defaultAutocompleteProps;

    if (newValue.length <= limitTags) {
      setData(newValue);
    } else {
      toast.error(`Максимально кол-во выбранных элементов ${limitTags}`);
    }
  };

  const groups = useMemo(() => {
    const uniqueGroups = new Set(options.map((o) => o.group).filter(Boolean));
    return Array.from(uniqueGroups);
  }, [options]);

  const deduplicatedOptions = useMemo(() => {
    const seen = new Set();
    return options.filter((o) => {
      const key = `${o.name}|${o.units}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [options]);

  const filteredOptions = useMemo(() => {
    const selectedIds = new Set(data.map((d) => d.sensorTypeId));
    let result = deduplicatedOptions;
    if (selectedGroup !== 'all') {
      result = result.filter((o) => o.group === selectedGroup);
    }
    return result.filter((o) => !selectedIds.has(o.sensorTypeId));
  }, [deduplicatedOptions, selectedGroup, data]);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <SensorGroupSelect
        groups={groups}
        selectedGroup={selectedGroup}
        onChange={(e) => setSelectedGroup(e.target.value)}
      />
      {isLoading ? (
        <Autocomplete
          {...defaultAutocompleteProps}
          options={[]}
          value={[]}
          loading
          readOnly
        />
      ) : (
        <Autocomplete
          key={`sensor-field-${data.length}`}
          {...defaultAutocompleteProps}
          options={filteredOptions}
          value={data}
          onChange={handleChange}
          isOptionEqualToValue={(option, value) => option.sensorTypeId === value.sensorTypeId}
          slotProps={{
            paper: {
              sx: {
                '& ul': {
                  maxHeight: '240px',
                },
              },
            },
          }}
        />
      )}
    </Box>
  );
}

SensorsField.propTypes = {
  // eslint-disable-next-line react/require-default-props
  data: arrayOf(
    shape({
      sensorTypeId: number,
      description: string,
      code: string,
      type: string,
      name: string,
      units: string,
    }),
  ),
  setData: func.isRequired,
  vehicleId: number.isRequired
};

export default React.memo(SensorsField, (prev, next) => {
  if (prev.data?.length !== next.data?.length) return false;
  if (prev.setData !== next.setData) return false;
  if (prev.vehicleId !== next.vehicleId) return false;
  return true;
});
